import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { studentsAPI } from '../lib/api';
import { bySurname } from '../lib/sort';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RequiredLabel } from '../components/RequiredLabel';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogActionBar } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usePagination, PaginationBar } from '../components/PaginationBar';
import { Plus, Search, Upload, GraduationCap, Hash, Pencil, Trash2, Download, ChevronDown, FileSpreadsheet, FileText, CheckCircle2, XCircle, Building2, TrendingUp } from 'lucide-react';
import PromoteCohortDialog from '../components/PromoteCohortDialog';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { downloadBlob, exportTableToPdf } from '../lib/exportUtils';
import { courseTypesAPI } from '../lib/api';

const PlacementPill = ({ info, testId }) => {
  if (!info) {
    return <span data-testid={testId} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">Unplaced</span>;
  }
  const confirmed = info.status === 'active';
  return (
    <Link
      to={info.cycle_id ? `/cycles/${info.cycle_id}` : '#'}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
      title={`${info.restaurant_name}${info.cycle_name ? ` · ${info.cycle_name}` : ''} · ${info.start_date} → ${info.end_date}`}
      className="inline-flex flex-col items-start gap-0.5 group/pill"
    >
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${confirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
        <Building2 className="w-3 h-3" /> {info.restaurant_name}
        <span className="opacity-70">· {confirmed ? 'confirmed' : 'pending'}</span>
      </span>
      {info.cycle_name && <span className="text-[10px] text-gray-500 truncate max-w-[220px] group-hover/pill:text-ica-navy group-hover/pill:underline">{info.cycle_name}</span>}
    </Link>
  );
};

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [placementFilter, setPlacementFilter] = useState('all');
  const [courseTypeFilter, setCourseTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [courseTypes, setCourseTypes] = useState([]);
  const [courseTypeLengths, setCourseTypeLengths] = useState({});
  const [formData, setFormData] = useState({
    student_number: '',
    name: '',
    email: '',
    phone: '',
    year_of_study: '1st Year',
    course_length: '3 Year',
    course_type: '',
    enrollment_date: '',
    skills: '',
    preferences: '',
    status: 'active',
  });

  useEffect(() => {
    fetchStudents();
    courseTypesAPI.detail().then(r => {
      setCourseTypes((r.data || []).map((c) => c.name));
      setCourseTypeLengths(Object.fromEntries((r.data || []).map((c) => [c.name, c.course_length])));
    }).catch(() => {});
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await studentsAPI.getAll();
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  // Export scope: the ticked students if any are selected, otherwise every
  // student matching the current search / filters (never the whole population
  // unless no filter is active).
  const exportScope = () => {
    const base = selectedIds.size > 0 ? sortedStudents.filter((s) => selectedIds.has(s.id)) : sortedStudents;
    const label = selectedIds.size > 0
      ? `${base.length} selected student${base.length === 1 ? '' : 's'}`
      : `${base.length} student${base.length === 1 ? '' : 's'}${base.length !== students.length ? ' (filtered)' : ''}`;
    return { rows: base, label };
  };

  const handleExportExcel = async () => {
    try {
      const { rows, label } = exportScope();
      const res = await studentsAPI.exportExcel(rows.length !== students.length ? rows.map((s) => s.id) : undefined);
      downloadBlob(res.data, `students_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Excel export downloaded — ${label}`);
    } catch {
      toast.error('Failed to export students');
    }
  };

  const handleExportPdf = async () => {
    try {
      const { rows: scope, label } = exportScope();
      const rows = scope
        .slice()
        .sort((a, b) => {
          const yearA = yearOrder[a.year_of_study] || 99;
          const yearB = yearOrder[b.year_of_study] || 99;
          if (yearA !== yearB) return yearA - yearB;
          return bySurname(a, b);
        })
        .map((s) => [
          s.year_of_study || '-',
          s.course_length || '-',
          s.course_type || '-',
          s.student_number || '-',
          s.name,
          s.email,
          s.phone || '-',
          s.enrollment_date || '-',
          s.status,
        ]);
      await exportTableToPdf({
        title: 'Mizanplaz — Students',
        subtitle: label,
        columns: ['Year', 'Course Length', 'Course Type', 'Student #', 'Name', 'Email', 'Phone', 'Enrollment Date', 'Status'],
        rows,
        filename: `students_${new Date().toISOString().slice(0, 10)}.pdf`,
        orientation: 'landscape',
      });
      toast.success(`PDF export downloaded — ${label}`);
    } catch {
      toast.error('Failed to export students PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
      };

      if (editingStudent) {
        await studentsAPI.update(editingStudent.id, data);
        toast.success('Student updated successfully');
      } else {
        await studentsAPI.create(data);
        toast.success('Student created successfully');
      }

      setIsModalOpen(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this student?')) return;

    try {
      await studentsAPI.delete(id);
      toast.success('Student deleted successfully');
      fetchStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const handleEdit = (studentOrEvent, studentArg) => {
    // Support both (student) and (e, student) call signatures
    let student;
    if (studentArg) {
      // Called as (e, student) from button click
      studentOrEvent.stopPropagation();
      student = studentArg;
    } else {
      // Called as (student) from row click
      student = studentOrEvent;
    }
    setEditingStudent(student);
    setFormData({
      student_number: student.student_number || '',
      name: student.name,
      email: student.email,
      phone: student.phone || '',
      year_of_study: student.year_of_study || '1st Year',
      course_length: student.course_length || '3 Year',
      course_type: student.course_type || '',
      enrollment_date: student.enrollment_date,
      skills: student.skills?.join(', ') || '',
      preferences: student.preferences || '',
      status: student.status,
    });
    setIsModalOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadResult(null);
    setUploading(true);
    try {
      const response = await studentsAPI.upload(file);
      const { imported = 0, updated = 0, errors = [] } = response.data;
      setUploadResult({ imported, updated, errors });
      if (imported + updated > 0) toast.success(`${imported} added · ${updated} updated`);
      if (errors.length > 0) toast.error(`${errors.length} row${errors.length === 1 ? '' : 's'} skipped — see details`);
      if (errors.length === 0) setIsUploadModalOpen(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const resetForm = () => {
    setEditingStudent(null);
    setFormData({
      student_number: '',
      name: '',
      email: '',
      phone: '',
      year_of_study: '1st Year',
      course_length: '3 Year',
      course_type: '',
      enrollment_date: '',
      skills: '',
      preferences: '',
      status: 'active',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      graduated: 'bg-blue-100 text-blue-700 border-blue-200',
      inactive: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[status] || variants.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getYearBadge = (year) => {
    const variants = {
      '1st Year': 'bg-blue-50 text-blue-700',
      '2nd Year': 'bg-purple-50 text-purple-700',
      '3rd Year': 'bg-ica-gold/10 text-ica-gold-dark',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${variants[year] || variants['1st Year']}`}>
        {year}
      </span>
    );
  };

  const filteredStudents = students.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.student_number && s.student_number.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchesYear = yearFilter === 'all' || s.year_of_study === yearFilter;
    const matchesPlacement = placementFilter === 'all' || (placementFilter === 'placed' ? !!s.placement_info : !s.placement_info);
    const matchesCourse = courseTypeFilter === 'all' || (s.course_type || '') === courseTypeFilter;
    return matchesSearch && matchesStatus && matchesYear && matchesPlacement && matchesCourse;
  });

  // Counts per year respecting search + status filter (but ignoring year itself)
  const yearScopedStudents = students.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.student_number && s.student_number.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchesPlacement = placementFilter === 'all' || (placementFilter === 'placed' ? !!s.placement_info : !s.placement_info);
    const matchesCourse = courseTypeFilter === 'all' || (s.course_type || '') === courseTypeFilter;
    return matchesSearch && matchesStatus && matchesPlacement && matchesCourse;
  });
  const yearCounts = {
    all: yearScopedStudents.length,
    '1st Year': yearScopedStudents.filter((s) => s.year_of_study === '1st Year').length,
    '2nd Year': yearScopedStudents.filter((s) => s.year_of_study === '2nd Year').length,
    '3rd Year': yearScopedStudents.filter((s) => s.year_of_study === '3rd Year').length,
  };

  const yearOrder = { '1st Year': 1, '2nd Year': 2, '3rd Year': 3 };

  const sortedStudents = filteredStudents
    .sort((a, b) => {
      const yearA = yearOrder[a.year_of_study] || 99;
      const yearB = yearOrder[b.year_of_study] || 99;
      if (yearA !== yearB) return yearA - yearB;
      const lenA = a.course_length || '3 Year';
      const lenB = b.course_length || '3 Year';
      if (lenA !== lenB) return lenA.localeCompare(lenB);
      const typeA = (a.course_type || '').toLowerCase();
      const typeB = (b.course_type || '').toLowerCase();
      if (typeA !== typeB) return typeA.localeCompare(typeB);
      return bySurname(a, b);
    });
  const pg = usePagination(sortedStudents, 'students');
  const pageIds = pg.pageItems.map((s) => s.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const toggleOne = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelectedIds((prev) => {
    const n = new Set(prev);
    allPageSelected ? pageIds.forEach((id) => n.delete(id)) : pageIds.forEach((id) => n.add(id));
    return n;
  });
  const selectAllFiltered = () => setSelectedIds(new Set(sortedStudents.map((s) => s.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await studentsAPI.bulkDelete([...selectedIds]);
      const { deleted, with_live_placements } = res.data;
      toast.success(`Deleted ${deleted} student${deleted === 1 ? '' : 's'}${with_live_placements ? ` · ${with_live_placements} had live placements (now orphaned)` : ''}`);
      clearSelection();
      setBulkDeleteOpen(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };
  const selectedWithPlacements = students.filter((s) => selectedIds.has(s.id) && s.placement_info).length;
  const groupedStudents = pg.pageItems
    .reduce((groups, student) => {
      const year = student.year_of_study || '1st Year';
      const length = student.course_length || '3 Year';
      const type = student.course_type || '';
      const key = `${year}|||${length}|||${type}`;
      if (!groups[key]) groups[key] = { year, length, type, students: [] };
      groups[key].students.push(student);
      return groups;
    }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="students-page" className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Students</h1>
            <p className="text-gray-500 mt-1">Manage your culinary students</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              data-testid="download-student-template-btn"
              variant="outline"
              onClick={async () => {
                try {
                  const res = await studentsAPI.downloadTemplate();
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a'); a.href = url;
                  a.download = 'student_upload_template.xlsx'; a.click();
                  window.URL.revokeObjectURL(url);
                } catch { toast.error('Failed to download template'); }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="export-students-btn"
                  variant="outline"
                 
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-students-excel" onClick={handleExportExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-students-pdf" onClick={handleExportPdf}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="promote-cohort-btn"
              variant="outline"
              onClick={() => setPromoteOpen(true)}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Promote cohort
            </Button>
            <Button
              data-testid="upload-students-btn"
              variant="outline"
              onClick={() => setIsUploadModalOpen(true)}
             
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Excel
            </Button>
            <Button
              data-testid="add-student-btn"
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="search-students"
              placeholder="Search by name, email, or student number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="graduated">Graduated</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={placementFilter} onValueChange={setPlacementFilter}>
            <SelectTrigger data-testid="placement-filter" className="w-40">
              <SelectValue placeholder="Placement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Placements</SelectItem>
              <SelectItem value="placed">Placed</SelectItem>
              <SelectItem value="unplaced">Unplaced</SelectItem>
            </SelectContent>
          </Select>
          <Select value={courseTypeFilter} onValueChange={setCourseTypeFilter}>
            <SelectTrigger data-testid="course-type-filter" className="w-52">
              <SelectValue placeholder="Course type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Course Types</SelectItem>
              {[...new Set([...courseTypes, ...students.map((s) => s.course_type).filter(Boolean)])].sort().map((ct) => (
                <SelectItem key={ct} value={ct} data-testid={`course-type-filter-opt-${ct}`}>{ct}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Year Tabs */}
        <Tabs value={yearFilter} onValueChange={setYearFilter} className="w-full">
          <TabsList data-testid="year-tabs" className="bg-white border border-gray-200 p-1 h-auto flex-wrap">
            <TabsTrigger
              data-testid="year-tab-all"
              value="all"
              className="data-[state=active]:bg-ica-navy data-[state=active]:text-white"
            >
              All Years
              <span className="ml-2 text-xs opacity-70">({yearCounts.all})</span>
            </TabsTrigger>
            <TabsTrigger
              data-testid="year-tab-1st"
              value="1st Year"
              className="data-[state=active]:bg-ica-navy data-[state=active]:text-white"
            >
              1st Year
              <span className="ml-2 text-xs opacity-70">({yearCounts['1st Year']})</span>
            </TabsTrigger>
            <TabsTrigger
              data-testid="year-tab-2nd"
              value="2nd Year"
              className="data-[state=active]:bg-ica-navy data-[state=active]:text-white"
            >
              2nd Year
              <span className="ml-2 text-xs opacity-70">({yearCounts['2nd Year']})</span>
            </TabsTrigger>
            <TabsTrigger
              data-testid="year-tab-3rd"
              value="3rd Year"
              className="data-[state=active]:bg-ica-navy data-[state=active]:text-white"
            >
              3rd Year
              <span className="ml-2 text-xs opacity-70">({yearCounts['3rd Year']})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Students Table */}
        <Card>
          <CardContent className="p-0">
            {filteredStudents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <GraduationCap className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-center">
                  {searchQuery || statusFilter !== 'all' || yearFilter !== 'all' || placementFilter !== 'all' || courseTypeFilter !== 'all'
                    ? `No students found${yearFilter !== 'all' ? ` in ${yearFilter}` : ''} matching your filters.`
                    : 'No students yet. Add your first student to get started.'}
                </p>
              </div>
            ) : (
              <>
              <PaginationBar {...pg.bar} position="top" noun="students" />
              {selectedIds.size > 0 && (
                <div data-testid="bulk-selection-bar" className="flex flex-wrap items-center gap-3 px-4 py-2 bg-ica-navy text-white text-sm">
                  <span data-testid="bulk-selection-count"><b>{selectedIds.size}</b> selected</span>
                  {selectedIds.size < sortedStudents.length && (
                    <button data-testid="bulk-select-all-filtered" onClick={selectAllFiltered} className="underline text-ica-gold-light hover:text-white text-xs">Select all {sortedStudents.length} matching</button>
                  )}
                  <button data-testid="bulk-clear-selection" onClick={clearSelection} className="underline text-gray-300 hover:text-white text-xs">Clear</button>
                  <Button data-testid="bulk-delete-btn" size="sm" variant="destructive" className="ml-auto h-8" onClick={() => setBulkDeleteOpen(true)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete selected
                  </Button>
                </div>
              )}
              {/* Phone: stacked cards */}
              <div className="sm:hidden divide-y" data-testid="students-card-list">
                {Object.entries(groupedStudents).map(([key, group]) => (
                  <React.Fragment key={key}>
                    <button
                      type="button"
                      className="w-full bg-ica-navy/5 px-4 py-2 flex items-center gap-2 text-left"
                      onClick={() => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <ChevronDown className={`w-4 h-4 text-ica-navy transition-transform shrink-0 ${collapsedGroups[key] ? '-rotate-90' : ''}`} />
                      <span className="font-semibold text-sm text-ica-navy">{group.year}</span>
                      <span className="text-xs text-gray-600 truncate">· {group.length} · {group.type || 'Unspecified'}</span>
                      <span className="text-xs text-gray-500 ml-auto shrink-0">{group.students.length}</span>
                    </button>
                    {!collapsedGroups[key] && group.students.map((student) => (
                      <div
                        key={student.id}
                        data-testid={`student-card-${student.id}`}
                        className={`px-4 py-3 flex items-start gap-3 active:bg-ica-gold/5 ${selectedIds.has(student.id) ? 'bg-ica-gold/10' : ''}`}
                        onClick={(e) => { if (e.target.closest('button') || e.target.closest('input')) return; handleEdit(student); }}
                      >
                        <input type="checkbox" data-testid={`select-student-card-${student.id}`} aria-label={`Select ${student.name}`} checked={selectedIds.has(student.id)} onChange={() => toggleOne(student.id)} className="h-4 w-4 mt-3 accent-[#1e3a5f] shrink-0" />
                        <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy font-semibold shrink-0">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">{student.name}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1"><Hash className="w-3 h-3" />{student.student_number || '-'}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {getYearBadge(student.year_of_study || '1st Year')}
                            {getStatusBadge(student.status)}
                            <PlacementPill info={student.placement_info} testId={`placement-pill-card-${student.id}`} />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button data-testid={`edit-student-card-${student.id}`} onClick={(e) => handleEdit(e, student)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`delete-student-card-${student.id}`} onClick={(e) => handleDelete(e, student.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
              <div className="table-scroll hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="py-3 pl-4 pr-1 w-8">
                        <input type="checkbox" data-testid="select-page-checkbox" aria-label="Select all on this page" checked={allPageSelected} onChange={togglePage} className="h-4 w-4 accent-[#1e3a5f] cursor-pointer" />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Year</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Course Length</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Course Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Student</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Student #</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Placement</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedStudents).map(([key, group]) => (
                      <React.Fragment key={key}>
                        <tr 
                          className="bg-ica-navy/5 cursor-pointer hover:bg-ica-navy/10 transition-colors"
                          onClick={() => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                        >
                          <td colSpan={9} className="py-2 px-4">
                            <div className="flex items-center gap-2">
                              <ChevronDown className={`w-4 h-4 text-ica-navy transition-transform ${collapsedGroups[key] ? '-rotate-90' : ''}`} />
                              <span className="font-semibold text-sm text-ica-navy">{group.year}</span>
                              <span className="mx-1 text-gray-300">|</span>
                              <span className="text-sm text-gray-600">{group.length} Course</span>
                              <span className="mx-1 text-gray-300">|</span>
                              <span className="text-sm text-gray-700">{group.type || 'Unspecified Course Type'}</span>
                              <span className="text-xs text-gray-500 ml-2">({group.students.length} students)</span>
                            </div>
                          </td>
                        </tr>
                        {!collapsedGroups[key] && group.students.map((student) => (
                          <tr 
                            key={student.id} 
                            data-testid={`student-row-${student.id}`} 
                            className={`border-b hover:bg-ica-gold/5 transition-colors cursor-pointer ${selectedIds.has(student.id) ? 'bg-ica-gold/10' : ''}`}
                            onClick={(e) => {
                              if (e.target.closest('button') || e.target.closest('input')) return;
                              handleEdit(student);
                            }}
                          >
                            <td className="py-4 pl-4 pr-1">
                              <input type="checkbox" data-testid={`select-student-${student.id}`} aria-label={`Select ${student.name}`} checked={selectedIds.has(student.id)} onChange={() => toggleOne(student.id)} className="h-4 w-4 accent-[#1e3a5f] cursor-pointer" />
                            </td>
                            <td className="py-4 px-4">
                              {getYearBadge(student.year_of_study || '1st Year')}
                            </td>
                            <td className="py-4 px-4">
                              <span className="text-xs font-medium text-gray-600">{student.course_length || '3 Year'}</span>
                            </td>
                            <td className="py-4 px-4">
                              <span className="text-xs font-medium text-gray-700">{student.course_type || <span className="text-gray-400 italic">—</span>}</span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-ica-navy/10 rounded-full flex items-center justify-center text-ica-navy font-semibold">
                                  {student.name.charAt(0).toUpperCase()}
                                </div>
                                <p className="font-medium text-gray-900">{student.name}</p>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Hash className="w-4 h-4" />
                                {student.student_number || '-'}
                              </div>
                            </td>
                            <td className="py-4 px-4"><PlacementPill info={student.placement_info} testId={`placement-pill-${student.id}`} /></td>
                            <td className="py-4 px-4">{getStatusBadge(student.status)}</td>
                            <td className="py-4 px-4">
                              <div className="flex gap-1">
                                <button
                                  data-testid={`edit-student-${student.id}`}
                                  onClick={(e) => handleEdit(e, student)}
                                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  data-testid={`delete-student-${student.id}`}
                                  onClick={(e) => handleDelete(e, student.id)}
                                  className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar {...pg.bar} position="bottom" noun="students" />
              </>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="student-modal">
            <DialogActionBar>
              <Button type="button" size="sm" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="cancel-student-top">Cancel</Button>
              <Button
                type="button"
                size="sm"
                data-testid="save-student-top"
                onClick={handleSubmit}
              >
                {editingStudent ? 'Update' : 'Create'}
              </Button>
            </DialogActionBar>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">
                {editingStudent ? 'Edit Student' : 'Add New Student'}
              </DialogTitle>
              <DialogDescription className="text-gray-500">
                {editingStudent ? 'Update student information below.' : 'Fill in the details to add a new student.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <RequiredLabel>Student Number</RequiredLabel>
                  <Input
                    data-testid="student-number"
                    value={formData.student_number}
                    onChange={(e) => setFormData({ ...formData, student_number: e.target.value })}
                    placeholder="e.g., STU-001"
                    required
                  />
                </div>
                <div>
                  <RequiredLabel>Year of Study</RequiredLabel>
                  <Select
                    value={formData.year_of_study}
                    onValueChange={(value) => setFormData({ ...formData, year_of_study: value })}
                  >
                    <SelectTrigger data-testid="student-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1st Year">1st Year</SelectItem>
                      <SelectItem value="2nd Year">2nd Year</SelectItem>
                      <SelectItem value="3rd Year">3rd Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <RequiredLabel>Course Length</RequiredLabel>
                  <Select
                    value={formData.course_length}
                    onValueChange={(value) => setFormData({ ...formData, course_length: value })}
                    disabled={!!courseTypeLengths[formData.course_type]}
                  >
                    <SelectTrigger data-testid="student-course-length">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 Year">1 Year Course</SelectItem>
                      <SelectItem value="2 Year">2 Year Course</SelectItem>
                      <SelectItem value="3 Year">3 Year Course</SelectItem>
                    </SelectContent>
                  </Select>
                  {courseTypeLengths[formData.course_type] && <p className="text-[10px] text-gray-500 mt-1">Set by the course type.</p>}
                </div>
                <div>
                  <Label>Course Type</Label>
                  <Select
                    value={formData.course_type || '__none__'}
                    onValueChange={(value) => {
                      const ct = value === '__none__' ? '' : value;
                      setFormData({ ...formData, course_type: ct, course_length: courseTypeLengths[ct] || formData.course_length });
                    }}
                  >
                    <SelectTrigger data-testid="student-course-type">
                      <SelectValue placeholder="Select a course type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {courseTypes.map((ct) => (
                        <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {courseTypes.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">No course types yet. Add some in Settings → Course Types.</p>
                  )}
                </div>
                <div className="col-span-2">
                  <RequiredLabel>Full Name</RequiredLabel>
                  <Input
                    data-testid="student-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <RequiredLabel>Email</RequiredLabel>
                  <Input
                    data-testid="student-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    data-testid="student-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <RequiredLabel>Enrollment Date</RequiredLabel>
                  <Input
                    data-testid="student-enrollment"
                    type="date"
                    value={formData.enrollment_date}
                    onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="student-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="graduated">Graduated</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Preferences</Label>
                  <Textarea
                    data-testid="student-preferences"
                    value={formData.preferences}
                    onChange={(e) => setFormData({ ...formData, preferences: e.target.value })}
                    placeholder="Any placement preferences..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-student-btn">
                  {editingStudent ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <PromoteCohortDialog open={promoteOpen} onOpenChange={setPromoteOpen} onDone={fetchStudents} />

        {/* Bulk delete confirm */}
        <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-red-700">Delete {selectedIds.size} student{selectedIds.size === 1 ? '' : 's'}?</DialogTitle>
              <DialogDescription className="text-gray-600">
                This permanently removes the selected student records. It cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {selectedWithPlacements > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="bulk-delete-placement-warning">
                <b>{selectedWithPlacements}</b> of the selected students currently have a live placement. Their placements will be left as orphans, which you can clear from the cycle's orphan badge.
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} data-testid="bulk-delete-cancel">Cancel</Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-confirm">
                <Trash2 className="w-4 h-4 mr-2" /> {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={(o) => { setIsUploadModalOpen(o); if (!o) setUploadResult(null); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Upload Students</DialogTitle>
              <DialogDescription className="text-gray-500">
                Upload an Excel file to bulk import students. Existing students (matched on student number, then email) are <b>updated</b>; new ones are added.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                The file should have columns:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>student_number, name, email, phone (optional)</li>
                <li>year_of_study, course_type (must exist under Settings → Course Types), course_length (optional — must match the course type)</li>
                <li>enrollment_date (sets the intake year), preferences, status (optional)</li>
              </ul>
              {uploadResult && (
                <div data-testid="upload-result" className="rounded-lg border bg-gray-50 p-3 space-y-2 text-sm">
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="upload-result-imported"><CheckCircle2 className="w-4 h-4" /> {uploadResult.imported} added</span>
                    <span className="inline-flex items-center gap-1 text-ica-navy font-medium" data-testid="upload-result-updated"><Pencil className="w-4 h-4" /> {uploadResult.updated} updated</span>
                    {uploadResult.errors.length > 0 && <span className="inline-flex items-center gap-1 text-red-700 font-medium" data-testid="upload-result-skipped"><XCircle className="w-4 h-4" /> {uploadResult.errors.length} skipped</span>}
                  </div>
                  {uploadResult.errors.length > 0 && (
                    <ul data-testid="upload-result-errors" className="max-h-48 overflow-y-auto space-y-1 text-xs text-red-700 bg-white border border-red-100 rounded p-2">
                      {uploadResult.errors.map((err, i) => <li key={i}>• {err}</li>)}
                    </ul>
                  )}
                  {uploadResult.errors.length > 0 && <p className="text-xs text-gray-500">Fix the rows above and upload the file again — rows already imported will simply be updated.</p>}
                </div>
              )}
              <div className={`border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-ica-gold transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <label className="cursor-pointer">
                  <span className="text-ica-navy font-medium hover:text-ica-gold">{uploading ? 'Uploading…' : 'Click to upload'}</span>
                  {!uploading && <span className="text-gray-500"> or drag and drop</span>}
                  <input
                    type="file"
                    data-testid="upload-students-input"
                    accept=".xlsx,.xls"
                    onChange={handleUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">Excel files only (.xlsx, .xls)</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Students;
